import React from 'react';

/**
 * The answer deck: quick replies promoted from a cramped chip strip above the
 * keyboard into the primary input, sized for one-handed tapping.
 *
 * Accepts plain strings (real quick_replies) or {key, label, send} objects —
 * the latter produced by extractEnumeratedOptions for quiz messages that carry
 * their options as numbered text lines. For those, the key chip shows the
 * number and tapping sends it, exactly as typing the number would.
 */

const KeyboardIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
    {...props}
  >
    <rect x="2.5" y="6" width="19" height="12" rx="2" />
    <path d="M7 10h.01M11 10h.01M15 10h.01M8 14h8" />
  </svg>
);

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

function normalizeItems(replies) {
  return replies.map((reply) =>
    typeof reply === 'string'
      ? { key: null, label: reply, send: reply }
      : reply
  );
}

/**
 * Letter keys help learners scan and aim, but they turn absurd when the
 * answers are themselves short tokens ("A) 1  B) 2"). Drop them whenever any
 * reply is already a bare number or single character.
 */
function deriveLetterKeys(items) {
  if (items.length > LETTERS.length) return items;
  const degenerate = items.some((item) =>
    /^[0-9]{1,2}$|^.$/.test(String(item.label).trim())
  );
  if (degenerate) return items;
  return items.map((item, index) => ({ ...item, key: LETTERS[index] }));
}

function AnswerDeck({ replies, onSelect, onTypeInstead, disabled }) {
  if (!replies?.length) return null;

  let items = normalizeItems(replies);
  // Items without explicit keys (real quick replies) get scannable letters;
  // extracted options already carry their number as the key.
  if (items.every((item) => item.key == null)) {
    items = deriveLetterKeys(items);
  }

  return (
    <div className="answer-deck">
      <div
        className="answer-deck-options"
        role="group"
        aria-label="Suggested answers"
      >
        {items.map((item) => (
          <button
            key={`${item.key ?? ''}:${item.label}`}
            type="button"
            className="answer-option"
            disabled={disabled}
            onClick={() => onSelect(item.send)}
          >
            {item.key != null && (
              <span className="answer-option-key" aria-hidden="true">
                {item.key}
              </span>
            )}
            <span className="answer-option-label">{item.label}</span>
          </button>
        ))}
      </div>

      <button
        type="button"
        className="answer-deck-alt"
        onClick={onTypeInstead}
        disabled={disabled}
      >
        <KeyboardIcon className="answer-deck-alt-icon" />
        Type your own answer
      </button>
    </div>
  );
}

export default AnswerDeck;
